import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChildren
} from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { UserCommonService } from 'app/modules/shared/services/user-common.service';
import { getCountryFlagStyle } from 'app/modules/shared/utils/country-flag.util';
import { AppSettings } from '../../modules/shared/app.settings';
import { CommonBindingDataService } from '../../modules/shared/services/common-binding-data.service';
import { UiService } from '../../modules/shared/services/ui.service';

export type LoginMethod = 'username' | 'emailOtp' | 'phoneOtp' | 'sso';

export interface LoginMethodOption {
  id: LoginMethod;
  labelKey: string;
  iconSrc: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  private static readonly OTP_RESEND_COOLDOWN_SECONDS = 30;
  private static readonly OTP_LENGTH = 4;
  readonly otpDigitIndexes = [0, 1, 2, 3];

  @Input() isSigningIn = false;
  usernamePlaceholder;
  passwordPlaceHolder;
  emailPlaceholder;
  phonePlaceholder;
  otpPlaceholder;
  defaultCountryCode = AppSettings.DEFAULT_COUNTRY_CODE;
  countryCodeList = AppSettings.COUNTRY_CODE;
  getCountryFlagStyle = getCountryFlagStyle;
  signInLabel;
  signInLabelWithSSO;
  sendOtpLabel;
  submitLabel;
  resendOtpLabel;
  resendOtpInLabel;
  changeLabel;
  @Output() onSignInEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onOtpSignInEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onForgotPasswordEmitter: EventEmitter<any> = new EventEmitter();
  passwordType = 'password';
  activeLoginMethod: LoginMethod = 'username';
  emailOtpSent = false;
  phoneOtpSent = false;
  emailOtpSending = false;
  phoneOtpSending = false;
  emailOtpResendCooldown = 0;
  phoneOtpResendCooldown = 0;
  emailOtpDigits: string[] = ['', '', '', ''];
  phoneOtpDigits: string[] = ['', '', '', ''];

  @ViewChildren('emailOtpDigitInput') emailOtpDigitInputs: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('phoneOtpDigitInput') phoneOtpDigitInputs: QueryList<ElementRef<HTMLInputElement>>;

  loginMethods: LoginMethodOption[] = [
    { id: 'username', labelKey: 'label_login_method_username', iconSrc: '/assets/images/temp/login-user.svg' },
    { id: 'emailOtp', labelKey: 'label_login_method_email_otp', iconSrc: '/assets/images/temp/login-tab-mail.svg' },
    { id: 'phoneOtp', labelKey: 'label_login_method_phone_otp', iconSrc: '/assets/images/temp/login-tab-phone.svg' },
    { id: 'sso', labelKey: 'label_login_method_sso', iconSrc: '/assets/images/temp/login-tab-sso.svg' },
  ];

  loginIconPaths = {
    user: '/assets/images/temp/login-input-user.svg',
    lock: '/assets/images/temp/login-lock.svg',
    email: '/assets/images/temp/login-input-mail.svg',
    phone: '/assets/images/temp/login-input-phone.svg',
    otp: '/assets/images/temp/login-key.svg',
    edit: '/assets/images/temp/icon-edit.svg',
    support: '/assets/images/temp/login-headset.svg',
    eyeOff: '/assets/images/temp/login-eye-off.svg'
  };

  signInForm = new UntypedFormGroup({
    username: new UntypedFormControl(),
    password: new UntypedFormControl()
  });

  emailOtpForm = new UntypedFormGroup({
    email: new UntypedFormControl(),
    otp: new UntypedFormControl()
  });

  phoneOtpForm = new UntypedFormGroup({
    numCountryCode: new UntypedFormControl(),
    phone: new UntypedFormControl(),
    otp: new UntypedFormControl()
  });

  private emailOtpCooldownTimer: ReturnType<typeof setInterval> | null = null;
  private phoneOtpCooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private commonBindingDataService: CommonBindingDataService,
    public userCommonService: UserCommonService,
    private storageService: StorageService,
    private uiService: UiService
  ) { }

  ngOnInit() {
    this.storageService.removeAll();
    this.storageService.clearSessionStorage();
    this.usernamePlaceholder = this.commonBindingDataService.getLabel('placeholder_enter_username');
    this.passwordPlaceHolder = this.commonBindingDataService.getLabel('placeholder_enter_password');
    this.emailPlaceholder = this.commonBindingDataService.getLabel('placeholder_enter_email');
    this.phonePlaceholder = this.commonBindingDataService.getLabel('placeholder_enter_phone');
    this.otpPlaceholder = this.commonBindingDataService.getLabel('placeholder_enter_otp');
    this.defaultCountryCode = AppSettings.DEFAULT_COUNTRY_CODE;
    this.countryCodeList = AppSettings.COUNTRY_CODE;
    this.signInLabel = this.commonBindingDataService.getLabel('label_sign_in');
    this.signInLabelWithSSO = this.commonBindingDataService.getLabel('label_login_sso');
    this.sendOtpLabel = this.commonBindingDataService.getLabel('label_send_otp');
    this.submitLabel = this.commonBindingDataService.getLabel('label_submit');
    this.resendOtpLabel = this.commonBindingDataService.getLabel('label_resend_otp');
    this.resendOtpInLabel = this.commonBindingDataService.getLabel('label_resend_otp_in');
    this.changeLabel = this.commonBindingDataService.getLabel('label_change');
    this.signInForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.maxLength(50)]],
      password: ['', [Validators.required]],
    });
    this.emailOtpForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(AppSettings.EMAIL_PATTERN)]],
      otp: ['', []],
    });
    this.phoneOtpForm = this.formBuilder.group({
      numCountryCode: [this.defaultCountryCode, [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern(AppSettings.COMMON_PHONE_NUMBER_REGEX)]],
      otp: ['', []],
    });
  }

  ngOnDestroy() {
    this.clearEmailOtpCooldown();
    this.clearPhoneOtpCooldown();
  }

  setLoginMethod(method: LoginMethod) {
    if (this.activeLoginMethod === method || this.isSigningIn) {
      return;
    }
    this.activeLoginMethod = method;
    this.resetEmailOtpState();
    this.resetPhoneOtpState();
  }

  isActiveMethod(method: LoginMethod): boolean {
    return this.activeLoginMethod === method;
  }

  doSignIn() {
    if (this.isSigningIn || this.signInForm.invalid) {
      this.signInForm.markAllAsTouched();
      return;
    }
    this.onSignInEmitter.emit(this.getLoginObject());
  }

  doSignInWithSSO() {
    if (!this.userCommonService.ssoFeature || this.isSigningIn) {
      return;
    }
    this.storageService.setSessionStorage('loggedInUserType', 'ssoUser');
    this.getLoginObjectForSSO();
  }

  getLoginObject() {
    return {
      username: this.signInForm.controls.username.value,
      password: this.commonBindingDataService.encrypt(this.signInForm.controls.password.value),
      tenantId: AppSettings.TENANT
    };
  }

  getLoginObjectForSSO() {
    window.location.href = `${AppSettings.BASE_URL}/${AppSettings.TENANT}/saml/ssologin`;
  }

  sendEmailOtp() {
    if (this.emailOtpSending || this.isSigningIn || (this.emailOtpSent && this.emailOtpResendCooldown > 0)) {
      return;
    }
    if (this.emailOtpForm.controls.email.invalid) {
      this.emailOtpForm.controls.email.markAsTouched();
      return;
    }
    this.emailOtpSending = true;
    const payload = {
      email: this.emailOtpForm.controls.email.value,
      tenantId: AppSettings.TENANT
    };
    this.userCommonService.sendOtpEmailAndPhone(JSON.stringify(payload)).subscribe((results) => {
      this.emailOtpSending = false;
      this.emailOtpSent = true;
      this.emailOtpForm.controls.email.disable({ emitEvent: false });
      this.clearOtpDigits('email');
      this.emailOtpForm.controls.otp.reset();
      this.setOtpValidators(this.emailOtpForm.controls.otp);
      this.startEmailOtpCooldown();
      this.focusOtpDigit('email', 0);
      const message = results?.general?.[0]?.message
        || this.commonBindingDataService.getLabel('msg_otp_sent_to_email');
      this.uiService.showMessage(message, AppSettings.MESSAGE_TYPE.SUCCESS);
    }, (error) => {
      this.emailOtpSending = false;
      this.showOtpErrorToast(error);
    });
  }

  sendPhoneOtp() {
    if (this.phoneOtpSending || this.isSigningIn || (this.phoneOtpSent && this.phoneOtpResendCooldown > 0)) {
      return;
    }
    if (this.phoneOtpForm.controls.numCountryCode.invalid || this.phoneOtpForm.controls.phone.invalid) {
      this.phoneOtpForm.controls.numCountryCode.markAsTouched();
      this.phoneOtpForm.controls.phone.markAsTouched();
      return;
    }
    this.phoneOtpSending = true;
    const payload = {
      phoneNum: this.phoneOtpForm.controls.phone.value,
      numCountryCode: this.phoneOtpForm.controls.numCountryCode.value,
      tenantId: AppSettings.TENANT
    };
    this.userCommonService.sendOtpEmailAndPhone(JSON.stringify(payload)).subscribe((results) => {
      this.phoneOtpSending = false;
      this.phoneOtpSent = true;
      this.phoneOtpForm.controls.phone.disable({ emitEvent: false });
      this.phoneOtpForm.controls.numCountryCode.disable({ emitEvent: false });
      this.clearOtpDigits('phone');
      this.phoneOtpForm.controls.otp.reset();
      this.setOtpValidators(this.phoneOtpForm.controls.otp);
      this.startPhoneOtpCooldown();
      this.focusOtpDigit('phone', 0);
      const message = results?.general?.[0]?.message
        || this.commonBindingDataService.getLabel('msg_otp_sent_to_phone');
      this.uiService.showMessage(message, AppSettings.MESSAGE_TYPE.SUCCESS);
    }, (error) => {
      this.phoneOtpSending = false;
      this.showOtpErrorToast(error);
    });
  }

  private showOtpErrorToast(error) {
    const message = this.getApiErrorMessage(error);
    if (message) {
      this.uiService.showMessage(message, AppSettings.MESSAGE_TYPE.ERROR);
    }
  }

  private getApiErrorMessage(error): string {
    if (!error) {
      return '';
    }
    for (const key of Object.keys(error)) {
      if (Array.isArray(error[key]) && error[key][0]?.message) {
        return error[key][0].message;
      }
    }
    return '';
  }

  changeEmailOtpIdentity() {
    if (this.isSigningIn) {
      return;
    }
    this.resetEmailOtpState(false);
  }

  changePhoneOtpIdentity() {
    if (this.isSigningIn) {
      return;
    }
    this.resetPhoneOtpState(false);
  }

  doEmailOtpSignIn() {
    if (this.isSigningIn || !this.emailOtpSent || this.emailOtpForm.invalid) {
      this.emailOtpForm.markAllAsTouched();
      return;
    }
    this.onOtpSignInEmitter.emit({
      email: this.emailOtpForm.controls.email.value,
      otp: this.emailOtpForm.controls.otp.value,
      tenantId: AppSettings.TENANT
    });
  }

  doPhoneOtpSignIn() {
    if (this.isSigningIn || !this.phoneOtpSent || this.phoneOtpForm.invalid) {
      this.phoneOtpForm.markAllAsTouched();
      return;
    }
    this.onOtpSignInEmitter.emit({
      phoneNum: this.phoneOtpForm.controls.phone.value,
      numCountryCode: this.phoneOtpForm.controls.numCountryCode.value,
      otp: this.phoneOtpForm.controls.otp.value,
      tenantId: AppSettings.TENANT
    });
  }

  onForgotPassword() {
    if (this.isSigningIn) {
      return;
    }
    this.onForgotPasswordEmitter.emit();
  }

  eventHandler(event) {
    if (event.keyCode === 13) {
      if (this.activeLoginMethod === 'username') {
        this.doSignIn();
      } else if (this.activeLoginMethod === 'emailOtp') {
        if (!this.emailOtpSent) {
          this.sendEmailOtp();
        } else {
          this.doEmailOtpSignIn();
        }
      } else if (this.activeLoginMethod === 'phoneOtp') {
        if (!this.phoneOtpSent) {
          this.sendPhoneOtp();
        } else {
          this.doPhoneOtpSignIn();
        }
      }
    }
  }

  onPhoneKeyPress(event) {
    const key = event.key;
    if (key === 'Enter') {
      this.eventHandler(event);
      return;
    }
    if (!/^[0-9]$/.test(key) && key !== 'Backspace' && key !== 'Delete' && key !== 'Tab'
      && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      event.preventDefault();
    }
  }

  onPhoneInput() {
    const phoneControl = this.phoneOtpForm.controls.phone;
    const sanitizedValue = (phoneControl.value || '').toString().replace(/\D/g, '');
    if (phoneControl.value !== sanitizedValue) {
      phoneControl.setValue(sanitizedValue, { emitEvent: false });
    }
  }

  onOtpDigitInput(channel: 'email' | 'phone', index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = this.getOtpDigits(channel);
    const sanitized = (input.value || '').replace(/\D/g, '');
    const value = sanitized.length > 1 ? sanitized.slice(-1) : sanitized;
    digits[index] = value;
    input.value = value;
    this.syncOtpControl(channel);
    if (value && index < LoginComponent.OTP_LENGTH - 1) {
      this.focusOtpDigit(channel, index + 1);
    }
  }

  onOtpDigitKeyDown(channel: 'email' | 'phone', index: number, event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.eventHandler(event);
      return;
    }
    if (event.key.length === 1 && !/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Backspace') {
      const digits = this.getOtpDigits(channel);
      if (digits[index]) {
        return;
      }
      if (index > 0) {
        event.preventDefault();
        digits[index - 1] = '';
        this.syncOtpControl(channel);
        const previousInput = this.getOtpDigitInputs(channel)?.get(index - 1)?.nativeElement;
        if (previousInput) {
          previousInput.value = '';
        }
        this.focusOtpDigit(channel, index - 1);
      }
    }
  }

  onOtpPaste(channel: 'email' | 'phone', event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, LoginComponent.OTP_LENGTH);
    if (!pasted) {
      return;
    }
    const digits = this.getOtpDigits(channel);
    for (let i = 0; i < LoginComponent.OTP_LENGTH; i++) {
      digits[i] = pasted[i] || '';
    }
    this.syncOtpControl(channel);
    const inputs = this.getOtpDigitInputs(channel);
    inputs?.forEach((inputRef, i) => {
      inputRef.nativeElement.value = digits[i] || '';
    });
    this.focusOtpDigit(channel, Math.min(pasted.length, LoginComponent.OTP_LENGTH - 1));
  }

  tooglePasswordType() {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }

  private resetEmailOtpState(clearEmail = true) {
    this.emailOtpSent = false;
    this.emailOtpSending = false;
    this.clearEmailOtpCooldown();
    this.emailOtpForm.controls.email.enable({ emitEvent: false });
    if (clearEmail) {
      this.emailOtpForm.controls.email.reset();
    }
    this.emailOtpForm.controls.otp.reset();
    this.emailOtpForm.controls.otp.clearValidators();
    this.emailOtpForm.controls.otp.updateValueAndValidity();
    this.clearOtpDigits('email');
  }

  private resetPhoneOtpState(clearPhone = true) {
    this.phoneOtpSent = false;
    this.phoneOtpSending = false;
    this.clearPhoneOtpCooldown();
    this.phoneOtpForm.controls.phone.enable({ emitEvent: false });
    this.phoneOtpForm.controls.numCountryCode.enable({ emitEvent: false });
    if (clearPhone) {
      this.phoneOtpForm.controls.phone.reset();
      this.phoneOtpForm.controls.numCountryCode.setValue(this.defaultCountryCode);
    }
    this.phoneOtpForm.controls.otp.reset();
    this.phoneOtpForm.controls.otp.clearValidators();
    this.phoneOtpForm.controls.otp.updateValueAndValidity();
    this.clearOtpDigits('phone');
  }

  private setOtpValidators(control: AbstractControl): void {
    control.setValidators([
      Validators.required,
      Validators.minLength(LoginComponent.OTP_LENGTH),
      Validators.maxLength(LoginComponent.OTP_LENGTH),
      Validators.pattern(/^\d{4}$/),
    ]);
    control.updateValueAndValidity();
  }

  private getOtpDigits(channel: 'email' | 'phone'): string[] {
    return channel === 'email' ? this.emailOtpDigits : this.phoneOtpDigits;
  }

  private getOtpDigitInputs(channel: 'email' | 'phone'): QueryList<ElementRef<HTMLInputElement>> {
    return channel === 'email' ? this.emailOtpDigitInputs : this.phoneOtpDigitInputs;
  }

  private syncOtpControl(channel: 'email' | 'phone'): void {
    const otpValue = this.getOtpDigits(channel).join('');
    const control = channel === 'email'
      ? this.emailOtpForm.controls.otp
      : this.phoneOtpForm.controls.otp;
    control.setValue(otpValue);
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
  }

  private clearOtpDigits(channel: 'email' | 'phone'): void {
    const digits = this.getOtpDigits(channel);
    for (let i = 0; i < LoginComponent.OTP_LENGTH; i++) {
      digits[i] = '';
    }
    this.getOtpDigitInputs(channel)?.forEach((inputRef) => {
      inputRef.nativeElement.value = '';
    });
  }

  private focusOtpDigit(channel: 'email' | 'phone', index: number): void {
    setTimeout(() => {
      const input = this.getOtpDigitInputs(channel)?.get(index)?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  private startEmailOtpCooldown() {
    this.clearEmailOtpCooldown();
    this.emailOtpResendCooldown = LoginComponent.OTP_RESEND_COOLDOWN_SECONDS;
    this.emailOtpCooldownTimer = setInterval(() => {
      this.emailOtpResendCooldown -= 1;
      if (this.emailOtpResendCooldown <= 0) {
        this.clearEmailOtpCooldown();
      }
    }, 1000);
  }

  private startPhoneOtpCooldown() {
    this.clearPhoneOtpCooldown();
    this.phoneOtpResendCooldown = LoginComponent.OTP_RESEND_COOLDOWN_SECONDS;
    this.phoneOtpCooldownTimer = setInterval(() => {
      this.phoneOtpResendCooldown -= 1;
      if (this.phoneOtpResendCooldown <= 0) {
        this.clearPhoneOtpCooldown();
      }
    }, 1000);
  }

  private clearEmailOtpCooldown() {
    if (this.emailOtpCooldownTimer) {
      clearInterval(this.emailOtpCooldownTimer);
      this.emailOtpCooldownTimer = null;
    }
    this.emailOtpResendCooldown = 0;
  }

  private clearPhoneOtpCooldown() {
    if (this.phoneOtpCooldownTimer) {
      clearInterval(this.phoneOtpCooldownTimer);
      this.phoneOtpCooldownTimer = null;
    }
    this.phoneOtpResendCooldown = 0;
  }
}
